import type { Fib, Item, Scores } from '@mas-flow/types';
import { wsjf } from '@mas-flow/domain';
import { DEFINITIONS, type Definition } from '../content/definitions';
import { DefinitionPopover } from './DefinitionPopover';
import { VoteDistribution } from './VoteDistribution';
import { useCastVote, useWithdrawVote } from '../hooks/useVote';

const FIBS: Fib[] = [1, 3, 5, 8, 13];

const SCORING_DIMS = DEFINITIONS.filter(
  (d): d is Definition & { id: keyof Scores } => d.id !== 'confidence',
);

export interface ScoringBlocksProps {
  item: Item;
  currentUserId: string;
  canSeeNames: boolean;
}

export function ScoringBlocks({ item, currentUserId, canSeeNames }: ScoringBlocksProps) {
  const cast = useCastVote();
  const withdraw = useWithdrawVote();
  const myVote = item.votes?.[currentUserId];
  const allVotes = Object.values(item.votes ?? {});
  const w = wsjf(item.scores);

  const onDim = (dim: keyof Scores, value: Fib) => {
    cast.mutate({ itemId: item.id, partial: { scores: { [dim]: value } } });
  };
  const onWithdraw = () => {
    if (myVote) withdraw.mutate({ itemId: item.id });
  };

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Score</h3>
        <div className="flex items-center gap-2">
          <span className="rounded bg-brand-wash px-2 py-0.5 text-sm text-brand-dark">
            WSJF {w == null ? '—' : w.toFixed(1)}
          </span>
          {myVote && (
            <button type="button" onClick={onWithdraw} className="text-xs text-slate-500 underline">
              Withdraw
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {SCORING_DIMS.map((def) => {
          const key = def.id;
          const myValue = myVote?.scores[key] ?? null;
          return (
            <div key={def.id}>
              <div className="flex items-center gap-1">
                <label className="text-xs font-medium text-slate-600">{def.title}</label>
                <DefinitionPopover definition={def} />
              </div>
              <p className="text-xs text-slate-400">{def.shortHelp}</p>
              <div className="mt-1 flex gap-1">
                {FIBS.map((v) => {
                  const active = myValue === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      aria-label={`Set my ${def.title} vote to ${v}`}
                      onClick={() => onDim(key, v)}
                      className={`min-w-[2rem] rounded border px-2 py-1 text-xs ${
                        active ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
              <div className="mt-1">
                <VoteDistribution dimension={key} votes={allVotes} canSeeNames={canSeeNames} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
